import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import LocaleStrings from "@language";
import { fetchLinkedInProfile, createSocialPost } from "@socialAction";
import { LOCAL_STORAGE, useLocalStorage } from "@useLocalStorage";
import {
  LINKEDIN_CLIENT_ID,
  FACEBOOK_APP_ID,
  COMMON_REDIRECT_URI,
} from "@constant";

export const useSocialMedia = () => {
  const dispatch = useDispatch();
  const session = useSelector((state) => state.session);
  const { setLocalStorage } = useLocalStorage();
  const [loading, setLoading] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const isCreatingPostRef = useRef(false);

  const openAuthWindow = (url, type) => {
    return new Promise((resolve) => {
      const authWindow = window.open(
        url,
        "_blank",
        "width=600,height=700,left=100,top=100,resizable,scrollbars"
      );

      const monitorWindow = setInterval(() => {
        try {
          if (!authWindow || authWindow.closed) {
            clearInterval(monitorWindow);
            resolve(null);
          }

          if (authWindow.location.hostname === window.location.hostname) {
            const params = new URLSearchParams(authWindow.location.search);
            authWindow.close();
            clearInterval(monitorWindow);
            resolve(handleAuthResponse(params, type));
          }
        } catch {}
      }, 1000);
    });
  };

  const handleAuthResponse = (params, type) => {
    return new Promise((resolve) => {
      if (type === "linkedin" && params.get("code")) {
        setLoading(true);
        if (isCreatingPostRef?.current) {
          resolve(params.get("code"));
          isCreatingPostRef.current = false;
        } else {
          dispatch(
            fetchLinkedInProfile(session, params.get("code"), (res) => {
              setLoading(false);
              if (res.success) {
                storeUserData(res.data);
                resolve(params.get("code"));
              } else {
                resolve(null);
              }
            })
          );
        }
      } else if (type === "facebook" && params.get("firstname")) {
        if (isCreatingPostRef?.current) {
          resolve(params.get("token"));
        } else {
          storeUserData(Object.fromEntries(params.entries()));
          resolve(params.get("firstname"));
        }
      } else {
        resolve(null);
      }
    });
  };

  const storeUserData = (data) => {
    const user = {
      firstname: data.firstname,
      lastname: data.lastname,
      username: data.name || data.username,
      profileurl: data.picture || data.profileurl,
      email: data.email,
    };
    setLocalStorage(LOCAL_STORAGE.USER_SOCIAL_DETAILS, user);
  };

  const handleLinkedInLogin = () =>
    openAuthWindow(
      `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${LINKEDIN_CLIENT_ID}&redirect_uri=${encodeURIComponent(
        COMMON_REDIRECT_URI
      )}&scope=email%20w_member_social%20profile%20openid&state=${encodeURIComponent(
        JSON.stringify({ currentURL: window.location.href, type: "linkedin" })
      )}`,
      "linkedin"
    );

  const handleFacebookLogin = () =>
    openAuthWindow(
      `https://www.facebook.com/v21.0/dialog/oauth?client_id=${FACEBOOK_APP_ID}&redirect_uri=${encodeURIComponent(
        COMMON_REDIRECT_URI
      )}&state=${encodeURIComponent(
        JSON.stringify({ currentURL: window.location.href, type: "facebook" })
      )}&scope=email,public_profile,user_posts`,
      "facebook"
    );

  const shareMediaPost = async (postData, type, callback) => {
    const loginFunctions = {
      facebook: handleFacebookLogin,
      linkedin: handleLinkedInLogin,
    };

    if (loginFunctions[type]) {
      isCreatingPostRef.current = true;
      const response = await loginFunctions[type]();
      if (response) {
        setPostLoading(true);
        const values = { code: response, ...postData };
        dispatch(
          createSocialPost(session, values, type, (res) => {
            setPostLoading(false);
            callback(res);
            const message = res.success
              ? `Successfully Posted on ${type}`
              : LocaleStrings.kSomethingWentWrongMsg;
            toast.success(message);
          })
        );
      }
    }
  };

  return {
    loading,
    postLoading,
    handleFacebookLogin,
    handleLinkedInLogin,
    shareMediaPost,
  };
};
